import type { DealKind } from '@carcheck/shared';
import { useState } from 'react';

import {
  addStage,
  canToggleKind,
  canToggleRequiresAct,
  makeStageKey,
  setStageField,
  sortedStageIndexes,
  swapStageOrder,
  toggleStageKind,
  type StagePatch,
} from './dealStagesEdit';
import type { RawRecord } from './rawRecord';

/**
 * AUT-633 — draft state of the `deals.stages` form (RULINGS 24). Every
 * update is functional, so two edits in one tick never overwrite each
 * other. The guards live here, not in the pure rules: a disabled MUI chip
 * still calls its handler (RULINGS 22), so the hook refuses the edit
 * itself. `saved` is the stored catalogue — the «Осмотр» chip reads it,
 * never the draft (RULINGS 7, 23).
 */

export interface StagesDraft {
  draft: RawRecord[];
  /** Array positions of the draft records in display order. */
  sorted: number[];
  /** True once a step was added: the last row's label takes the focus. */
  focusLast: boolean;
  edit: (key: string, patch: StagePatch) => void;
  toggleKind: (entry: RawRecord, kind: DealKind) => void;
  setRequiresAct: (entry: RawRecord, on: boolean) => void;
  move: (pos: number, by: -1 | 1) => void;
  add: () => void;
}

const keyOf = (entry: RawRecord): string => (typeof entry.key === 'string' ? entry.key : '');

export const useStagesDraft = (
  saved: RawRecord[],
  makeKey: () => string = makeStageKey,
): StagesDraft => {
  const [draft, setDraft] = useState<RawRecord[]>(saved);
  const [focusLast, setFocusLast] = useState(false);

  const edit = (key: string, patch: StagePatch): void =>
    setDraft((d) => setStageField(d, key, patch));

  const toggleKind = (entry: RawRecord, kind: DealKind): void => {
    if (!canToggleKind(saved, entry, kind)) return;
    setDraft((d) => toggleStageKind(d, keyOf(entry), kind));
  };

  const setRequiresAct = (entry: RawRecord, on: boolean): void => {
    if (on && !canToggleRequiresAct(entry)) return;
    edit(keyOf(entry), { requiresAct: on });
  };

  const move = (pos: number, by: -1 | 1): void =>
    setDraft((d) => {
      const order = sortedStageIndexes(d);
      const i = order[pos];
      const j = order[pos + by];
      return i === undefined || j === undefined ? d : swapStageOrder(d, i, j);
    });

  const add = (): void => {
    setDraft((d) => addStage(d, makeKey));
    setFocusLast(true);
  };

  return {
    draft,
    sorted: sortedStageIndexes(draft),
    focusLast,
    edit,
    toggleKind,
    setRequiresAct,
    move,
    add,
  };
};
